import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { postCheckout } from "@/lib/api";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("houndoz_cart")) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    localStorage.setItem("houndoz_cart", JSON.stringify(items));
  }, [items]);

  const addItem = (item) => {
    const key = `${item.product_id}-${item.size || ""}-${item.colour || ""}`;
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) =>
          i.key === key ? { ...i, quantity: i.quantity + item.quantity } : i
        );
      }
      return [...prev, { ...item, key }];
    });
    setOpen(true);
    toast.success(`${item.title} added to your cart`);
  };

  const updateQty = (key, qty) => {
    if (qty < 1) return;
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, quantity: qty } : i)));
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const clear = () => setItems([]);

  const total = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);
  const SHIPPING_FEE = 9.95;
  const FREE_SHIPPING_THRESHOLD = 150;
  const freeShipping = total >= FREE_SHIPPING_THRESHOLD;
  const shipping = !items.length || freeShipping ? 0 : SHIPPING_FEE;
  const freeShippingRemaining = freeShipping ? 0 : FREE_SHIPPING_THRESHOLD - total;
  const grandTotal = total + shipping;

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = items.map((i) => ({
        variant_id: i.variant_id || null,
        product_id: i.product_id,
        title: i.title,
        variant_title: [i.size, i.colour].filter(Boolean).join(" / "),
        price: i.price,
        quantity: i.quantity,
        preorder: !!i.preorder,
      }));
      const res = await postCheckout(payload);
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        toast.success(res.message || "Selection saved.");
      }
    } catch (e) {
      toast.error("Something went wrong at checkout. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CartContext.Provider
      value={{ items, addItem, updateQty, removeItem, clear, total, shipping, freeShipping, freeShippingRemaining, grandTotal, count, open, setOpen, checkout, loading }}
    >
      {children}
    </CartContext.Provider>
  );
};
